import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { Button } from '../../atoms/Button';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { MediaCard, } from '../../molecules/MediaCard/MediaCard';
export const HorizontalMediaCardSlider = forwardRef(function HorizontalMediaCardSlider({ mediaCardsProps, maxWidth = 1200, jump: initJump, startIndex, }, ref) {
    const [jump, setJump] = useState(initJump);
    const targetSlideRef = useRef(null);
    const wrapperRef = useRef(null);
    const isMultipleElements = Array.isArray(mediaCardsProps);
    const isMobile = useIsMobile();
    const touched = useRef(false);
    const cardWidth = targetSlideRef?.current?.clientWidth;
    const [targetSlide, setTargetSlide] = useState(startIndex);
    const scrollToTargetSlide = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (wrapper && targetSlideRef.current) {
            targetSlideRef.current.scrollIntoView({ behavior: 'smooth' });
            // you can also try a logic where the width is used if this is ever needed:
            // wrapper.scrollTo({
            //   top: 0,
            //   left: (targetSlide - 1) * targetSlideRef.current.clientWidth,
            //   behavior: 'smooth',
            // });
        }
    }, []);
    /**
     * This is needed in case the view is smaller than expected
     * Adjust the startIndex of the targetSlide if not touched
     * Adjust the jump - reduces the jump if view is smaller then expected
     */
    useEffect(() => {
        const adjustedTargetSlide = cardWidth
            ? Math.max(0, Math.floor(maxWidth / cardWidth) - 1)
            : startIndex;
        if (!touched.current) {
            setTargetSlide(adjustedTargetSlide);
        }
        if (adjustedTargetSlide !== startIndex) {
            setJump(Math.max(1, adjustedTargetSlide - 1));
        }
        else {
            setJump(initJump);
        }
    }, [cardWidth, initJump, jump, maxWidth, startIndex]);
    const moveLeft = useCallback((targetSlide) => {
        touched.current = true;
        if (!isMultipleElements || mediaCardsProps.length === 0) {
            return 0;
        }
        return Math.max(0, targetSlide - jump * 2 + 1);
    }, [isMultipleElements, mediaCardsProps, jump]);
    const moveRight = useCallback((targetSlide) => {
        touched.current = true;
        if (!isMultipleElements || mediaCardsProps.length === 0) {
            return 0;
        }
        const adjustedTargetSlide = startIndex > targetSlide ? startIndex : targetSlide;
        return Math.min(adjustedTargetSlide + jump, mediaCardsProps.length - 1);
    }, [isMultipleElements, jump, mediaCardsProps, startIndex]);
    useEffect(scrollToTargetSlide, [scrollToTargetSlide, targetSlide]);
    return (_jsxs(Stack, { direction: "row", alignItems: "center", ref: ref, sx: {
            maxWidth,
        }, children: [isMultipleElements && !isMobile && (_jsx(ArrowButton, { onClick: () => setTargetSlide(moveLeft), disabled: !touched.current || targetSlide === 0, leftRight: "left" })), _jsx(Box, { sx: {
                    overflowX: 'auto',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                }, ref: wrapperRef, children: _jsx(Stack, { direction: "row", gap: 106, children: isMultipleElements ? (mediaCardsProps.map((mediaCardProps, i) => {
                        return (_jsx(MediaCard, { ...mediaCardProps, ref: i === targetSlide ? targetSlideRef : null }, mediaCardProps.id));
                    })) : (_jsx(MediaCard, { ...mediaCardsProps })) }) }), isMultipleElements && !isMobile && (_jsx(ArrowButton, { onClick: () => setTargetSlide(moveRight), disabled: targetSlide >= mediaCardsProps.length - 1, leftRight: "right" }))] }));
});
const ArrowButton = ({ onClick, disabled, leftRight }) => {
    return (_jsx(Box, { sx: leftRight === 'left' ? { marginRight: '-8px' } : { marginLeft: '-8px' }, children: _jsx(Button, { icon: leftRight === 'left' ? 'arrow-left-1' : 'arrow-right-1', autoHeight: false, onClick: onClick, disabled: disabled, round: true, size: "large" }) }));
};
